import './styles.scss';

import { Button, Col, InputGroup, Row } from 'react-bootstrap';
import { Datetime, createNotification } from '@/utils';
import DeferredFileUpload, { DeferredFileUploadHandle } from '@/components/FileUpload/deferred-file-upload';
import {
  DispatchIssueRootCauseEnum,
  DispatchIssueWithCacheFlightLateEnum,
  DispatchIssueWithCacheFlightTypeEnum,
  DispatchIssueWithCacheRecoveryCompletedEnum,
  DispatchIssueWithCacheRecoveryNeededEnum,
  DispatchIssueWithCacheRootCauseEnum,
  DispatchIssueWithCacheTaxiDetailsEnum,
  DispatchIssueWithCacheTaxiUsedEnum,
} from '@/models/gen/graphql';
import { DispatcherDropdownField, DriverDropdownField, LeaderDropdownField } from '@/components/UserDropdown';
import { EnumDropdownField, getItemsFromEnum } from '@/components/EnumDropdown';
import GenericFileUpload, { GenericFile, GenericFileType } from '@/components/FileUpload/new';
import { useMemo, useRef, useState } from 'react';

import { AirportDropdownField } from '@/components/AirportDropdown';
import { AssignVehicleDropdownField } from '@/components/VehicleDropdown';
import { CurrencyInputField } from '@/components/CurrencyInput';
import { DATETIME_FORMAT } from '@/constants';
import { DatetimeInputField } from '@/components/DatetimeInput';
import { FlightNumberOrLoopNameInputField } from '@/common/FlightNumberInput';
import Form from '@/common/Form';
import { InputField } from '@/components/Input';
import IssueFormHistoryTable from '@/features/IssueHistory/components/IssueHistoryForm/IssueFormHistoryTable';
import { IssueHistoryFormDefinitions } from './types';
import { LoadingBlur } from '@/components/LoadingSpinner';
import { PhoneInputField } from '@/components/MaskedInput/PhoneInput';
import { TextAreaField } from '@/components/TextArea';
import { Toast } from '@/models';
import { createDispatchIssue } from '@/features/IssueHistory/api/services/createDispatchIssue';
import { createDispatchIssueCausedBy } from '../../api/services/createDispatchIssueCausedBy';
import { createDispatchIssueDocumentPresignedUrl } from '../../api/services/createDispatchIssueDocumentPresignedUrlEndpoint';
import { deleteDispatchIssueCausedBy } from '../../api/services/deleteDispatchIssueCausedBy';
import { deleteDispatchIssueDocument } from '../../api/services/deleteDispatchIssueDocument';
import { recreateDispatchIssueDocumentDownloadUrl } from '@/features/IssueHistory/api/services/createDispatchIssueDocumentDownloadURL';
import { updateDispatchIssue } from '@/features/IssueHistory/api/services/updateDispatchIssue';
import { updateDispatchIssueCausedBy } from '../../api/services/updateDispatchIssueCausedBy';
import useConfirmation from '@/hooks/useConfirmation';
import { useIssueHistory } from '@/features/IssueHistory';
import { useIssueHistoryDrawer } from '@/features/IssueHistory/components/IssueHistoryDrawer';
import useLocale from '@/hooks/useLocale';
import { useNavigate } from 'react-router-dom';
import { useServiceIssueFilters } from '@/hooks/useServiceIssueFilters';

type CausedByState = {
  deletedIds: string[];
};

const initCausedByState: CausedByState = {
  deletedIds: [],
};

const IssueHistoryForm = ({
  onSubmit,
  locale: customLocale,
  value = {},
  trip,
  onEditDispatchIssue,
}: IssueHistoryFormDefinitions.Props): JSX.Element => {
  const [, setState, { submitFilters }] = useIssueHistory();
  const setIssueHistoryDrawer = useIssueHistoryDrawer(({ setState }) => setState);
  const [causedByState, setCausedByState] = useState<CausedByState>(initCausedByState);
  const updateCausedBy = useRef<Record<string, string>>({});
  const [documents, setDocuments] = useState(value.documents ?? []);
  const [loading, setLoading] = useState(false);
  const confirmDocumentDelete = useConfirmation();
  const handleDeleteDocument = (id: string) => {
    confirmDocumentDelete()
      .then(async () => {
        try {
          await deleteDispatchIssueDocument([{ id }]);
          value.documents = value.documents.filter((document) => document.id !== id);
          setDocuments((current) => current.filter((document) => document.id !== id));
          createNotification('Successfully Deleted Document', Toast.Type.SUCCESS, 'Dispatch Issue');
          await submitFilters?.(useServiceIssueFilters.getState().state); // Because we aren't resubmitting on closing the form
        } catch (e) {
          console.error('Error Deleting Document', e);
          createNotification('Could Not Delete Document', Toast.Type.DANGER, 'Dispatch Issue');
        }
      })
      .catch(() => console.log('Cancelled document delete'));
  };
  const locale = useLocale({
    ...IssueHistoryFormDefinitions.getLocale(value),
    ...customLocale,
  });
  const fileUploadRef = useRef<DeferredFileUploadHandle>(null);
  const navigate = useNavigate();

  const handleSubmit = async (data) => {
    try {
      setLoading(true);
      const { causedByForm, causedBy, addCausedBy, ...rest } = data;
      // Check if we need to update or create any causedBy entries
      let updatedCausedBy = [];
      let newCausedBy = [];
      Object.keys(updateCausedBy?.current ?? {})?.map((causedById) => {
        // If the causedBy has an ID that exists, we need to update it
        const foundIndex = value.causedBy?.findIndex((causeBy) => causeBy.id === causedById);
        if (foundIndex >= 0) {
          const cause = value.causedBy?.[foundIndex];
          updatedCausedBy = [
            ...updatedCausedBy,
            {
              id: cause.id,
              dispatchIssueId: cause.dispatchIssueId,
              causedById: updateCausedBy.current[cause.id],
            },
          ];
        } else {
          // Otherwise we need to create it
          newCausedBy = [
            ...newCausedBy,
            {
              causedById: updateCausedBy.current[causedById],
              dispatchIssueId: data.id || '',
            },
          ];
        }
      });
      if (rest.id) {
        await updateDispatchIssue([rest]);
        createNotification('Sucessfully Updated Dispatch Issue', Toast.Type.SUCCESS, 'Dispatch Issue');
        if (updatedCausedBy.length > 0) {
          await updateDispatchIssueCausedBy(updatedCausedBy.filter((cause) => !causedByState.deletedIds.includes(cause.id)));
          createNotification('Sucessfully Updated Dispatch Issue Caused By', Toast.Type.SUCCESS, 'Dispatch Issue');
        }
        if (newCausedBy.length > 0) {
          await createDispatchIssueCausedBy(newCausedBy);
          createNotification('Sucessfully Created Dispatch Issue Caused By', Toast.Type.SUCCESS, 'Dispatch Issue');
        }
        setState((current) => ({
          ...current,
          issues: current.issues.set(rest.id, {
            ...current.issues.get(rest.id),
            ...rest,
          }),
        }));
      } else {
        const filesToUpload = fileUploadRef.current?.getFilesToUpload() ?? [];
        const { id, ...create } = rest;
        const createRes = await createDispatchIssue([
          { ...create, documents: filesToUpload.map((file) => ({ ...file, issueId: '', fileLocation: '' })), causedBy: newCausedBy || [] },
        ]);
        createNotification('Sucessfully Created Dispatch Issue', Toast.Type.SUCCESS, 'Dispatch Issue');
        await fileUploadRef.current?.performDeferredUploads(createRes?.[0]?.output?.document ?? []); // No bulk create, so we only get back one issue
      }
      // Check if we have any new comments to delete
      const deletedIds = causedByState.deletedIds.filter((id) => id !== null);
      if (deletedIds.length > 0) {
        await deleteDispatchIssueCausedBy(deletedIds.map((id) => ({ id })));
      }
    } catch (err) {
      console.error('Error Creating Dispatch Issue.', err);
      createNotification('Could Not Create Dispatch Issue', Toast.Type.DANGER, 'Dispatch Issue');
    } finally {
      await submitFilters?.(useServiceIssueFilters.getState().state);
      setLoading(false);
      onSubmit?.(data);
    }
  };
  const gotoTrips = () => {
    navigate('/trips');
    setIssueHistoryDrawer((current) => ({
      ...current,
      show: false,
    }));
  };
  const fullValue = useMemo(
    () => ({
      ...IssueHistoryFormDefinitions.initValue,
      ...value,
      ...IssueHistoryFormDefinitions.loadForm(),
    }),
    []
  );

  return (
    <Form
      name="DispatchIssueHistoryForm"
      className="IssueHistoryForm"
      onSubmit={handleSubmit}
      value={fullValue}
      validator={IssueHistoryFormDefinitions.validator(locale)}
      onChange={IssueHistoryFormDefinitions.saveForm}
    >
      <LoadingBlur loading={loading} />
      <Form.Input type="hidden" name="id" />
      <Form.Input type="hidden" name="tripId" />
      <Form.Input type="hidden" name="station" />
      <Row className="IssueHistoryFormHeader">
        <Col>
          {value.tripId && (
            <>
              <span>
                {value.tripServicerIataAirlineCode}
                {value.tripLoopName || value.tripFlightNumber.toString().padStart(4, '0')}{' '}
                {new Datetime(value.tripScheduled).format(DATETIME_FORMAT)}
              </span>
              <span>
                {value.station} - {[value.driverFirstName, value.driverLastName].join(' ')}
              </span>
            </>
          )}
          <h4 className="IssueHistoryFormTitle">{locale('Dispatch Issues')}</h4>
        </Col>
      </Row>
      {!value.tripId && !value?.id && (
        <Row className="IssueHistoryFormLinkTrips">
          <Col>
            <span>THIS IS NOT LINKED TO TRIP.</span>
            <span>
              WOULD YOU LIKE TO LINK THIS TO A TRIP?{' '}
              <Button variant="text" onClick={gotoTrips}>
                YES
              </Button>
            </span>
          </Col>
        </Row>
      )}
      <Row>
        <Col xs={6}>
          <Form.Input
            as={EnumDropdownField}
            name="rootCause"
            label={locale('Root Cause')}
            enum={getItemsFromEnum(DispatchIssueWithCacheRootCauseEnum)}
            validate
          />
        </Col>
        <Col xs={6}>
          <Form.Input as={DatetimeInputField} name="datetime" label={locale('Issue Date/Time')} validate />
        </Col>
      </Row>
      <Form.Watch name={['causedByForm', 'rootCause']}>
        {([causedByRecords, rootCause]: [Record<string, string>, DispatchIssueRootCauseEnum], form) => {
          updateCausedBy.current = causedByRecords as Record<string, string>;
          const handleAddCausedBy = (causedById: string) => {
            if (!causedById) return;
            if (Object.values(causedByRecords).includes(causedById)) {
              createNotification('Dispatcher already added', Toast.Type.WARNING, 'Dispatch Issue');
              return;
            }
            form.setValue(`causedByForm.${causedById}`, causedById);
          };
          const handleRemoveCausedBy = (cause) => {
            let id = null;
            // If there are no causedBy records, we can just use the cause directly as we haven't created any yet
            // If it cause is also a key then we can use it directly as it hasn't been created yet
            if (value.causedBy === undefined || Object.keys(causedByRecords).includes(cause)) {
              id = cause;
            } else {
              id = value.causedBy?.find((causeToFind) => causeToFind.causedById === cause)?.id || null;
            }
            const current = form.getValues(`causedByForm`);
            delete current[id];
            form.setValue(`causedByForm`, current || {}, { shouldValidate: true });
            setCausedByState((current) => ({
              ...current,
              deletedIds: [...current.deletedIds, id],
            }));
          };
          if (rootCause !== DispatchIssueRootCauseEnum.Dispatch) return null;
          return (
            <>
              {Object.entries(causedByRecords ?? {}).map(([key, causedById]) => {
                return (
                  <Row key={key}>
                    <Col>
                      <InputGroup>
                        <Form.Input
                          as={DispatcherDropdownField}
                          name={`causedByForm.${key}`}
                          label={locale('Caused By Dispatcher')}
                          value={causedById}
                          onChange={(e) => {
                            form.setValue(`causedByForm.${key}`, e);
                          }}
                        />
                        <Button
                          variant="icon"
                          onClick={() => handleRemoveCausedBy(causedById)}
                          className="IssueHistoryFormRemoveCausedByButton"
                        >
                          <i className="sv sv-trash2 text-danger" />
                        </Button>
                      </InputGroup>
                    </Col>
                  </Row>
                );
              })}
              <Row>
                <Col>
                  <Form.Input
                    as={DispatcherDropdownField}
                    name="addCausedBy"
                    label={locale('Dispatcher Who Caused Issue')}
                    placeholder={locale('Dispatcher')}
                    onChange={handleAddCausedBy}
                    value=""
                  />
                </Col>
              </Row>
            </>
          );
        }}
      </Form.Watch>
      <Row>
        <Col>
          <Form.Input
            as={TextAreaField}
            name="rootCauseDescription"
            label={locale('Root Cause Notes')}
            placeholder={locale('Detailed Explanation')}
            validate
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Input as={TextAreaField} name="flightIssueDescription" label={locale('Description Of Flight Issue')} validate />
        </Col>
      </Row>
      {!value.tripId && (
        <Row>
          <Col>
            <Form.Input as={AirportDropdownField} name="station" label={locale('Station')} validate />
          </Col>
        </Row>
      )}
      <Row>
        <Col>
          <Form.Input
            as={EnumDropdownField}
            name="flightType"
            label={locale('Flight Type')}
            enum={getItemsFromEnum(DispatchIssueWithCacheFlightTypeEnum)}
            options={{
              locale: {
                Arr: locale('Arrival'),
                Dep: locale('Departure'),
              },
            }}
            validate
          />
        </Col>
      </Row>
      <Form.Watch name="station">
        {([airport]: [string]) => (
          <Row>
            <Col xs={6}>
              <Form.Input as={DriverDropdownField} airports={airport} name="driverId" label={locale('Driver Name')} />
            </Col>
            <Col xs={6}>
              <Form.Input
                as={AssignVehicleDropdownField}
                name="vehicleId"
                label={locale('Van Number')}
                airportCode={airport}
                disabled={!airport}
              />
            </Col>
          </Row>
        )}
      </Form.Watch>
      <Row>
        <Col xs={6}>
          <Form.Input as={DispatcherDropdownField} name="dispatcherId" label={locale('Dispatcher')} validate />
        </Col>
        <Col xs={6}>
          <Form.Input
            as={EnumDropdownField}
            name="flightLate"
            label={locale('Did The Plane Push Late?')}
            enum={getItemsFromEnum(DispatchIssueWithCacheFlightLateEnum)}
            validate
          />
        </Col>
      </Row>
      <Form.Watch name="flightLate">
        {([flightLate]: [DispatchIssueWithCacheFlightLateEnum]) =>
          flightLate === DispatchIssueWithCacheFlightLateEnum.Yes && (
            <Row>
              <Col xs={6}>
                <Form.Input as={DatetimeInputField} name="scheduledTime" label={locale('SCHED TIME')} validate />
              </Col>
              <Col xs={6}>
                <Form.Input
                  as={DatetimeInputField}
                  name="pushTime"
                  label={locale('PUSH TIME')}
                  enum={getItemsFromEnum(DispatchIssueWithCacheFlightLateEnum)}
                  validate
                />
              </Col>
            </Row>
          )
        }
      </Form.Watch>
      <Row>
        <Col xs={6}>
          <Form.Input
            as={EnumDropdownField}
            name="recoveryNeeded"
            label={locale('Is Recovery Needed?')}
            enum={getItemsFromEnum(DispatchIssueWithCacheRecoveryNeededEnum)}
            validate
          />
        </Col>
        <Col xs={6}>
          <Form.Input as={InputField} name="crewMemberGiftCardCount" label={locale('# Of Crew For Gift Card')} type="number" />
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Form.Input
            as={EnumDropdownField}
            name="recoveryCompleted"
            label={locale('Is Recovery Complete?')}
            enum={getItemsFromEnum(DispatchIssueWithCacheRecoveryCompletedEnum)}
          />
        </Col>
        <Col xs={6}>
          <Form.Input
            as={EnumDropdownField}
            name="taxiDetails"
            label={locale('Taxi Details')}
            enum={getItemsFromEnum(DispatchIssueWithCacheTaxiDetailsEnum)}
            options={{
              locale: {
                Crewpaid: locale('Crew Paid'),
                Shgpaid: locale('SHG Paid'),
                Shgtaxi: locale('SHG Taxi'),
              },
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Form.Input
            as={EnumDropdownField}
            name="taxiUsed"
            label={locale('Used Taxi/Lyft?')}
            enum={getItemsFromEnum(DispatchIssueWithCacheTaxiUsedEnum)}
          />
        </Col>
        <Col xs={6}>
          <Form.Input as={CurrencyInputField} name="taxiCost" label={locale('Cost Of Taxi/Lyft')} />
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Form.Input as={FlightNumberOrLoopNameInputField} name="recoveryFlightName" label={locale('Recovery Flight #')} />
        </Col>
        <Col xs={6}>
          <Form.Input as={DatetimeInputField} name="recoveryFlightDatetime" label={locale('Recovery Date/Time')} />
        </Col>
      </Row>
      {value.tripId && (
        <Row>
          <Col>
            <Form.Input as={TextAreaField} name="outboundFlightInfo" label={locale('Outbound Flight Information')} validate />
          </Col>
        </Row>
      )}
      <Row>
        <Col>
          <Form.Input as={LeaderDropdownField} name="managerId" label={locale('Which Mod Notified For Recovery')} />
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Form.Input as={InputField} name="crewMemberName" label={locale('Crew Member Name')} />
        </Col>
        <Col xs={6}>
          <Form.Input as={PhoneInputField} name="crewMemberPhone" label={locale('Crew Member Phone')} />
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Button name="SUBMIT" type="submit" variant={locale('SUBMIT_BUTTON_COLOR')}>
            {locale('SUBMIT_BUTTON_TEXT')}
          </Form.Button>
        </Col>
      </Row>
      {value.id && (
        <>
          <Row className="IssueHistoryFormUpload">
            <Col>
              <GenericFileUpload
                endpoint={async (file: GenericFileType) => {
                  try {
                    const payload = {
                      filename: file.file.name,
                      md5: file.md5,
                      fileSize: file.file.size,
                      issueId: value.id,
                    };
                    const res = await createDispatchIssueDocumentPresignedUrl(payload);
                    createNotification('Sucessfully Uploaded Document', Toast.Type.SUCCESS, 'Dispatch Issue');
                    return [res?.url, res?.contentType];
                  } catch (e) {
                    console.error('Error Uploading Document', e);
                    createNotification('Could Not Upload Document', Toast.Type.DANGER, 'Dispatch Issue');
                  }
                }}
                options={{
                  showFiles: true,
                  multiple: true,
                }}
                locale={{
                  Upload: 'UPLOAD FILES',
                }}
                onError={async (...args) => console.log('', ...args)}
                onSuccess={async (...args) => {
                  await submitFilters?.(useServiceIssueFilters.getState().state);
                  const documents = await recreateDispatchIssueDocumentDownloadUrl({ issueId: [value.id] });
                  const res = documents.urls.map((document) => {
                    const md5Match = document.url.match(/dispatch\/([a-f0-9]{32})\//); // Extract md5 from URL
                    const md5 = md5Match ? md5Match[1] : null;
                    return {
                      md5,
                      url: document.url,
                    };
                  });
                  return res;
                }}
              />
            </Col>
          </Row>
          {documents?.map((document) => (
            <Row className="pb-2" key={document.id}>
              <Col>
                <GenericFile name={document.filename} preview={document.fileLocation} onDelete={() => handleDeleteDocument(document.id)} />
              </Col>
            </Row>
          ))}
        </>
      )}
      {!value.id && (
        <>
          <Row className="IssueHistoryFormUpload">
            <Col>
              <DeferredFileUpload ref={fileUploadRef} options={{ multiple: true, showFiles: true }} />
            </Col>
          </Row>
        </>
      )}
      {value.tripId && (
        <>
          <Row className="IssueHistoryFormHistory">
            <Col>
              <h4 className="IssueHistoryFormHistoryTitle">{locale('Dispatch Issue History')}</h4>
            </Col>
          </Row>
          <Row>
            <Col>
              <IssueFormHistoryTable
                onEditDispatchIssue={onEditDispatchIssue}
                id={value.tripId}
                driverFirstName={trip?.driver?.firstName}
                driverLastName={trip?.driver?.lastName}
                vehicleTrackingId={trip?.vehicle?.trackingId}
              />
            </Col>
          </Row>
        </>
      )}
    </Form>
  );
};

export default IssueHistoryForm;
